import React, { useState } from 'react'
import {
  useModalContext,
  Button,
  get,
  Label,
  TextBody,
  Space,
  Box,
  TextHeading,
  ButtonGroup,
  Alert,
  CheckboxStateful,
  useFocus,
  useTriggerSnackbar,
} from '@ally/metronome-ui'
import styled from 'styled-components'
import { useFormsContext } from '@ally/forms'
import { Private } from '@ally/private'
import allytm from '@ally/allytm'
import { Beneficiary, BeneficiaryAddress, IraPlan } from '../../../types'
import { GENERIC_ERROR_MESSAGE, ZIP_CODE_ERROR } from '../../../utils/constants'

import {
  bulletedLastFour,
  getErrorMessage,
  maskSSN,
  removeLeadingZeros,
  formatDOBforInput,
} from '../../../utils/formatter'
import { useIraBeneficiaries } from '../../../services/IraBeneficiariesProvider'
import { AllocationStepIraValues } from '../../IraBeneficiaries/AddBeneficiaryFlow/AllocationStepIra'
import { useBeneficiariesApi } from '../../../api/beneficiaries-api'
import { KeyValuePairObj, fireErrorTagging } from '../../../utils/tagging'

const headerRef = React.createRef<HTMLHeadingElement>()

const AllocationWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${get.space('sm')};
  margin-bottom: ${get.space('sm')};
`

const StyledButtonGroup = styled(ButtonGroup)`
  margin-top: ${get.space('md')};
  margin-bottom: ${get.space('md')};
`

export const EditBeneficiaryVerifyPage: React.FC<{
  accountId: string
  beneficiaryId: string
  beneficiaries: Beneficiary[] | undefined
  stepBack: (message: string) => void
}> = ({ beneficiaryId, beneficiaries, accountId, stepBack }) => {
  const { iraPlans } = useIraBeneficiaries()
  const { setOpen } = useModalContext()
  const [hasSignatureValidationError, setSignatureValidationError] =
    useState(false)
  const [errorRef, focusErrorRef] = useFocus()
  const [isChecked, setIsChecked] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const { values } = useFormsContext()
  const [errorMessage, setErrorMessage] = useState('')
  const { updateIraBeneficiary } = useBeneficiariesApi()
  const { triggerSnackbar } = useTriggerSnackbar()
  const { getIraBeneficiaries } = useIraBeneficiaries()

  // istanbul ignore next - chaining option
  const currentBeneficiary = beneficiaries?.find(
    bene => bene.beneficiaryId === beneficiaryId,
  )

  // istanbul ignore next - low value
  if (!currentBeneficiary || !beneficiaries) return <></>
  const {
    lastName,
    firstName,
    middleName,
    relationshipToOwner,
    beneficiaryType,
    dateOfBirth,
    ssn,
  } = currentBeneficiary

  const { addressLine1, addressLine2, city, state, zipCode } = values

  const filteredIraBenes = beneficiaries.filter(
    bene => bene.beneficiaryType === beneficiaryType,
  )

  const currentPlan =
    iraPlans && iraPlans.find((plan: IraPlan) => plan.planId === accountId)

  const getCustomerAddress = (): BeneficiaryAddress => {
    return {
      addressLine1: addressLine1 as string,
      addressLine2: addressLine2 || '',
      city: city as string,
      state: state as string,
      zipCode: zipCode as string,
      country: 'US',
    }
  }

  const createPayloadObject = (): any => {
    const allBenePayload = filteredIraBenes
      ? filteredIraBenes
          .filter(bene => bene.beneficiaryId !== beneficiaryId)
          .map(bene => ({
            beneficiaryId: bene.beneficiaryId,
            allocationPercentage: removeLeadingZeros(
              values[bene.beneficiaryId as keyof AllocationStepIraValues],
            ),
          }))
      : // istanbul ignore next // low value
        []
    return {
      beneficiaries: [
        {
          address: getCustomerAddress(),
          allocationPercentage: removeLeadingZeros(values[beneficiaryId]),
          beneficiaryId,
        },
        ...allBenePayload,
      ],
    }
  }

  const onSubmit = async (): Promise<void> => {
    if (isChecked) {
      setIsLoading(true)
      const payload = createPayloadObject()
      try {
        setErrorMessage('')
        await updateIraBeneficiary(accountId, payload)
        await getIraBeneficiaries()
        // istanbul ignore next
        triggerSnackbar({
          text: 'You updated your beneficiaries.',
          columnSize: 4,
          snackbarButtonText: 'Close',
          onClose: () =>
            requestAnimationFrame(() => {
              document.getElementById(`${accountId}-heading`)?.focus()
            }),
        })
        // istanbul ignore next - low value
        setIsLoading(false)
        // istanbul ignore next - tagging
        allytm.event('customEvent', {
          name: 'formSuccess',
          type: 'formValidationSuccess',
          attr: {
            formValues: {
              form: 'Edit IRA Beneficiary',
              values: [],
            },
          },
        })
      } catch (err: any) {
        const errorMes = getErrorMessage(err)
        if (errorMes === 'USB_BUS_351') {
          allytm.event('customError', ZIP_CODE_ERROR, errorMes)
          setIsLoading(false)
          stepBack(ZIP_CODE_ERROR)
        } else {
          setErrorMessage(GENERIC_ERROR_MESSAGE)
          allytm.event('customError', GENERIC_ERROR_MESSAGE, '')
          setIsLoading(false)
        }
      }
    } else {
      setSignatureValidationError(true)
      fireErrorTagging({
        beneficiaryVerify: 'Check this box to continue.',
      } as KeyValuePairObj)
      requestAnimationFrame(focusErrorRef)
    }
  }

  return (
    <Space mb="md">
      <TextHeading
        tag="h1"
        size="sm"
        weight="bold"
        ref={headerRef}
        tabIndex={-1}
        data-testid="verifyBeneModalHeader"
        id="add-bene-heading"
      >
        Verify beneficiary
      </TextHeading>
      <>
        {errorMessage && (
          <Box mb="md">
            <Alert
              variant="error"
              focusOnMount
              disableContainer
              body={errorMessage}
            />
          </Box>
        )}
        {currentPlan && (
          <Box mt="md" mb="md">
            <Label htmlFor="plan">Plan</Label>
            <TextBody tag="span" size="sm">
              {currentPlan.planType} IRA Plan{' '}
              {bulletedLastFour(currentPlan.planNumber)}
            </TextBody>
          </Box>
        )}

        <Box mb="sm">
          <Label htmlFor="beneficiaryName">Beneficiary Name</Label>
          <Private>
            <TextBody tag="span" size="sm">
              {`${firstName} ${middleName || ''} ${lastName}`}
            </TextBody>
          </Private>
        </Box>

        <Box mb="sm">
          <Label htmlFor="dateOfBirth">Date of Birth</Label>
          <Private>
            <TextBody tag="span" size="sm">
              {dateOfBirth ? formatDOBforInput(dateOfBirth) : '-/-'}
            </TextBody>
          </Private>
        </Box>

        <Box mb="sm">
          <Label htmlFor="socialSecurityNumber">Social Security Number</Label>
          <Private>
            <TextBody tag="span" size="sm">
              {maskSSN(ssn as string)}
            </TextBody>
          </Private>
        </Box>
        {relationshipToOwner && (
          <Box mb="sm">
            <Label htmlFor="relationshipToOwner">Relationship</Label>
            <Private>
              <TextBody tag="span" size="sm">
                {relationshipToOwner}
              </TextBody>
            </Private>
          </Box>
        )}
        {beneficiaryType && (
          <Box mb="sm">
            <Label htmlFor="beneficiaryType">Beneficiary Type</Label>
            <Private>
              <TextBody tag="span" size="sm">
                {`${beneficiaryType[0].toUpperCase()}${beneficiaryType.slice(
                  1,
                )}`}
              </TextBody>
            </Private>
          </Box>
        )}

        <Box mb="sm">
          <Label htmlFor="fundsAllocation">Funds Allocation</Label>
          {filteredIraBenes &&
            filteredIraBenes.map((bene: Beneficiary) => (
              <AllocationWrapper key={bene.beneficiaryId}>
                <TextBody tag="div" size="sm">
                  <Private>
                    {`${bene.firstName} ${
                      bene.middleName ? bene.middleName : ''
                    } ${bene.lastName}`}
                  </Private>
                </TextBody>
                <TextBody tag="div" size="sm">
                  {`${removeLeadingZeros(values[bene.beneficiaryId])}%`}
                </TextBody>
              </AllocationWrapper>
            ))}
        </Box>

        <Box mb="md" mr="md" mt="lg">
          <Box mb="sm">
            {hasSignatureValidationError && (
              <Alert
                variant="error"
                disableContainer
                body="Check this box to continue."
                ref={errorRef}
              />
            )}
          </Box>
          <CheckboxStateful
            label="By checking this box, I sign and agree that the information I provided is true and accurately reflects my choices."
            name="authorizationCheckbox"
            onChange={({ target: { checked } }): void => {
              if (checked) {
                setSignatureValidationError(false)
              }
              setIsChecked(checked)
            }}
            data-testid="verify-request-checkbox"
          />
        </Box>

        <StyledButtonGroup>
          <Button
            allytmln="Submit Edit Verify Beneficiary"
            text="Submit"
            onClick={onSubmit}
            loading={isLoading}
            data-testid="submit-continue-button"
          />
          <Button
            allytmln="Cancel Edit Beneficiary"
            text="Cancel"
            onClick={(): void => setOpen(false)}
            variant="link"
            data-testid="cancel-edit-button-verify"
          />
        </StyledButtonGroup>
      </>
    </Space>
  )
}
